import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ShipCard.css';

const ShipCard = ({ passagePlan }) => {
  const { ship, destination, enterAt, direction, viaWaypoint, withPilot, status } = passagePlan;
  const navigate = useNavigate();

  const handleShipNameClick = () => {
    navigate(`/ship/${ship.id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '未定';
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDirectionText = (direction) => {
    if (!direction) return '不明';
    switch (direction) {
      case 'north':
        return '北航船';
      case 'south':
        return '南航船';
      default:
        return '不明';
    }
  };

  const getDestinationLabel = (direction) => {
    if (!direction) return '入港/出港';
    switch (direction) {
      case 'north':
        return '入港';
      case 'south':
        return '出港';
      default:
        return '入港/出港';
    }
  };

  const getShipTypeIcon = (kind) => {
    const defaultIcon = '/static/default-ship-icon.png';

    // アイコンURLが提供されている場合はそれを使用
    if (kind?.iconUrl) {
      return (
        <img
          src={kind.iconUrl}
          alt={kind.name || '船種'}
          className="ship-type-icon"
          onError={(e) => {
            // 画像の読み込みに失敗した場合はデフォルト画像を表示
            e.target.src = defaultIcon;
          }}
        />
      );
    }

    // iconUrlがない場合はデフォルト画像を表示
    return (
      <img
        src={defaultIcon}
        alt={kind?.name || '船種'}
        className="ship-type-icon"
      />
    );
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'fresh':
        return { text: '新規', color: '#4CAF50', icon: '✨' };
      case 'alive':
        return { text: '有効', color: '#2196F3', icon: '✅' };
      case 'updated':
        return { text: '変更あり', color: '#FF9800', icon: '🔄' };
      case 'ongoing':
        return { text: '進行中', color: '#9C27B0', icon: '🚀' };
      case 'expired':
        return { text: '期限切れ', color: '#F44336', icon: '⏰' };
      case 'deleted':
        return { text: '削除済み', color: '#757575', icon: '🗑️' };
      case 'ambiguous':
        return { text: '不明確', color: '#FF5722', icon: '❓' };
      case 'unknown':
      default:
        return { text: '不明', color: '#9E9E9E', icon: '❓' };
    }
  };

  const statusInfo = getStatusInfo(status);

  return (
    <div className="ship-card">
      <div className="ship-card-header">
        <div className="ship-icon">
          {getShipTypeIcon(ship.kind)}
        </div>
        <div className="ship-info">
          <h3
            className="ship-name clickable"
            onClick={handleShipNameClick}
          >
            {ship.name || '船名不明'}
          </h3>
          <p className="ship-type">{ship.kind?.name || '船種不明'}</p>
        </div>
        <div className="ship-badges">
          {ship.huge && <span className="huge-flag">巨大船</span>}
        </div>
      </div>

      <div className="ship-details">
        <div className="detail-row">
          <span className="detail-label">旗国:</span>
          <span className="detail-value flag-nation">
                    {ship.flagNation?.imageUrl && (
                      <img
                        src={ship.flagNation.imageUrl}
                        alt={ship.flagNation.name || '旗国'}
                        className="flag-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
            <span className="flag-text">
              {ship.flagNation?.name || '不明'}
              {ship.flagNation?.abbreviation && ` (${ship.flagNation.abbreviation})`}
            </span>
          </span>
        </div>

        {ship.length && (
          <div className="detail-row">
            <span className="detail-label">長さ:</span>
            <span className="detail-value">{ship.length}m</span>
          </div>
        )}

        {ship.tonnage && (
          <div className="detail-row">
            <span className="detail-label">トン数:</span>
            <span className="detail-value">{ship.tonnage.toLocaleString()}t</span>
          </div>
        )}
      </div>

      <div className="passage-info">
        <div className="passage-header">
          <h4>航行情報</h4>
          <span className={`direction-badge ${direction === 'north' ? 'north' : direction === 'south' ? 'south' : 'unknown'}`}>
            {getDirectionText(direction)}
          </span>
        </div>

        <div className="detail-row">
          <span className="detail-label">{getDestinationLabel(direction)}:</span>
          <span className="detail-value">{destination?.name || '不明'}</span>
        </div>

        <div className="detail-row">
          <span className="detail-label">入航予定:</span>
          <span className="detail-value">{formatDate(enterAt)}</span>
        </div>

        <div className="passage-flags">
          <span
            className="status-badge"
            style={{ backgroundColor: statusInfo.color }}
          >
            {statusInfo.icon} {statusInfo.text}
          </span>
          {viaWaypoint && (
            <span className="flag via-waypoint">中ノ瀬</span>
          )}
          {withPilot && (
            <span className="flag with-pilot">水先人</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShipCard;
